import { Notification } from '@schibsted-svp/react-ui';
import { getNewsroomConfig } from 'config';
import { reportMessage } from 'lib/error';
import { useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useFetchAssetsQuery } from 'services/svp-api-proxy-rtk/assets';
import { getAsset } from 'store/assets/selectors';
import type { z } from 'zod';
import type { PlaylistItem } from './types';
import { themeFormSchema, themeResolver } from './validator';

export type ThemeFormData = z.infer<typeof themeFormSchema>;

export const INITIAL_THEME_FORM: ThemeFormData = {
    themeTitle: '',
    hideIcon: false,
    background: {
        lightModeColor: null,
        darkModeColor: null,
    },
    videoCards: {
        textColor: null,
        highlightColor: null,
    },
    images: {
        iconLight: null,
        iconDark: null,
        backgroundImageLight: null,
        backgroundImageDark: null,
    },
};

export const useThemeForm = (initialValues: ThemeFormData) =>
    useForm<ThemeFormData>({
        mode: 'all',
        defaultValues: initialValues,
        resolver: themeResolver,
    });

// TODO: replace with actual BFF call when backend is ready
const saveTheme = async (): Promise<void> => {};

export const useThemeFormSubmit = ({
    formApi,
    onSuccess = () => {},
}: {
    formApi: UseFormReturn<ThemeFormData>;
    onSuccess?: (values: ThemeFormData) => void;
}) => {
    const { handleSubmit } = formApi;

    return useMemo(
        () =>
            handleSubmit(async (data: ThemeFormData) => {
                try {
                    await saveTheme();
                    Notification.notify.success('Theme saved');
                    onSuccess(data);
                } catch (error) {
                    reportMessage({ message: 'Failed to save theme', error });
                    Notification.notify.error('Failed to save theme');
                }
            }),
        [handleSubmit, onSuccess]
    );
};

export function usePlaylistDuration({
    items,
    isRegularPlaylist,
}: {
    items: PlaylistItem[];
    isRegularPlaylist: boolean;
}) {
    const playlistAssets = useSelector((state) =>
        items.map((item) => 'asset' in item && getAsset(state, { id: item.asset.id, provider: item.asset.provider }))
    );

    if (!isRegularPlaylist) {
        return 0;
    }

    return playlistAssets.reduce((accumulator, currentAsset) => accumulator + Number(currentAsset?.duration) || 0, 0);
}

export function useFetchPlaylistAssetsViews({
    provider,
    items,
    skip,
}: {
    provider: string;
    items: PlaylistItem[] | undefined;
    skip?: boolean;
}) {
    const { features } = getNewsroomConfig(provider);

    const assetsIds = items
        ?.reduce<number[]>((acc, item) => {
            if ('asset' in item && item.asset.provider === provider) {
                return [...acc, item.asset.id];
            }
            return acc;
        }, [])
        ?.sort();

    useFetchAssetsQuery(
        { provider, assetId: assetsIds },
        { skip: skip || !features.playlistViews?.enabled || !assetsIds?.length }
    );
}
